import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import { FC, useCallback } from "react";
import styled from "@emotion/styled";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { ExportItem } from "@/pages/settings/components/data-export";
import {
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSButton,
  MDSButtonSlotName,
  MDSTextColor,
  MDSIcon,
  MDSIconKind,
} from "@/mds";

export const SettingsDataExportSection: FC = observer(() => {
  const { store } = useAppStore();
  const exportIsRunning = store.dataExports.isExportInProgress;
  const exportsAreReady = store.dataExports.isReady;

  const latestModelData = store.dataExports.latestExport?.modelData;
  const said = latestModelData?.owned_by_space_account_id ?? "no_space_account";
  const eid = store.dataExports.latestExport?.id ?? "no_export_id";
  const jobUrl = `https://cloud.temporal.io/namespaces/prod-backend-async-worker.rqp4d/workflows/ExportDataForSpaceAccountWorkflow-${said}-${eid}`;

  const handleExportClick = useCallback(async () => {
    trackEvent(TrackedEvent.ExportCreate);
    store.dataExports.createExport();
  }, [store.dataExports]);

  return (
    <ContentWrapper>
      <MDSText variant={MDSTextVariant.ParagraphS}>
        Export your notes and collections as Markdown files
      </MDSText>
      <LineSeparator />
      <MDSText variant={MDSTextVariant.ParagraphS} weight={MDSTextWeight.Bold}>
        How it works
      </MDSText>
      <MDSText variant={MDSTextVariant.ParagraphS} as="div">
        <StyledList>
          <li>Your individual notes will each become a separate Markdown file</li>
          <li>
            Collections will become Markdown files that include all notes from that collection
          </li>
          <li>
            Any images or attachments in your notes will be saved in an &quot;attachments&quot;
            folder
          </li>
          <li>Everything will be packaged in a zip file ready for download</li>
        </StyledList>
      </MDSText>

      {exportsAreReady && (
        <>
          <StyledButton onClick={handleExportClick} isDisabled={exportIsRunning}>
            <MDSIcon kind={MDSIconKind.ArrowDownToLine} slot={MDSButtonSlotName.LeftIcon} />
            Export all notes and collections
          </StyledButton>
          <SectionWrapper>
            <MDSText variant={MDSTextVariant.ParagraphS} weight={MDSTextWeight.Bold}>
              Your Exports
            </MDSText>
            <ExportsContainer>
              {store.dataExports.latestExport?.isRunning && store.debug.debugModeEnabled && (
                <DebugJobUrl>
                  <a href={jobUrl} target="_blank" rel="noreferrer">
                    DEBUG: Temporal Job
                  </a>
                </DebugJobUrl>
              )}
              {store.dataExports.getLatestExports(3).map(dataExport => (
                <ExportItem dataExport={dataExport} key={dataExport.id} />
              ))}
              {store.dataExports.length === 0 && (
                <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Tertiary}>
                  Your export history will appear here
                </MDSText>
              )}
            </ExportsContainer>
          </SectionWrapper>
        </>
      )}
    </ContentWrapper>
  );
});

const ExportsContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "start",
  gap: 8,
  width: "100%",
});

const DebugJobUrl = styled.div({
  paddingLeft: 12,
});

const LineSeparator = styled.div`
  height: ${({ theme }) => theme.lineHeights["paragraph-s"]};
`;

const StyledList = styled.ul`
  margin: 0;
  padding-left: 20px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledButton = styled(MDSButton)`
  margin-top: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[5]};
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`;
